/**
 * Persist a local MT Spider / RIP watch folder and write print-ready sheets into it.
 * Uses the File System Access API (Chrome / Edge on the print PC).
 */

const DB_NAME = "riverr-nester";
const STORE = "handles";
const HANDLE_KEY = "dtfHotFolder";

export const canWriteHotFolder = () =>
  typeof window !== "undefined" &&
  typeof window.showDirectoryPicker === "function";

const openHandleDb = () =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

export const loadHotFolderHandle = async () => {
  try {
    const db = await openHandleDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(HANDLE_KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return null;
  }
};

export const saveHotFolderHandle = async (handle) => {
  if (!handle) return;
  const db = await openHandleDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(handle, HANDLE_KEY);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
  });
};

export const ensureHotFolderPermission = async (handle) => {
  if (!handle?.queryPermission || !handle.requestPermission) return false;
  const opts = { mode: "readwrite" };
  const current = await handle.queryPermission(opts);
  if (current === "granted") return true;
  const next = await handle.requestPermission(opts);
  return next === "granted";
};

export const pickHotFolder = async () => {
  if (!canWriteHotFolder()) return null;
  const handle = await window.showDirectoryPicker({
    id: "riverr-dtf-hotfolder",
    mode: "readwrite",
    startIn: "documents",
  });
  if (!(await ensureHotFolderPermission(handle))) return null;
  await saveHotFolderHandle(handle);
  return handle;
};

export const resolveHotFolder = async ({ forcePick = false } = {}) => {
  if (!canWriteHotFolder()) return null;
  if (!forcePick) {
    const saved = await loadHotFolderHandle();
    if (saved && (await ensureHotFolderPermission(saved))) return saved;
  }
  try {
    return await pickHotFolder();
  } catch (err) {
    if (err && (err.name === "AbortError" || err.name === "NotAllowedError")) {
      return null;
    }
    throw err;
  }
};

export const writeHotFolderFile = async (dirHandle, fileName, blob) => {
  if (!dirHandle?.getFileHandle) {
    throw new Error("No printer folder selected");
  }
  const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  try {
    await writable.write(blob);
  } finally {
    await writable.close();
  }
};
